import {
  LucideArrowDownWideNarrow,
  LucideArrowUpDown,
  LucideArrowUpNarrowWide,
  LucideCheck,
  LucideStar,
  LucideX,
  type LucideIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchesQuery } from '~/api'
import { aUser, useAtomValue } from '~/atoms'
import { ControlTooltip, LoadingCircle } from '~/components'
import { Button, Input, Popover, Sheet } from '~/components/ui'
import { useLocalStorage } from '~/hooks'
import { cn } from '~/utils'
import type { AppMapRef } from '../google'
import { SavedSearchesDialog, SavedSearchesList } from './searches'

type SortField = 'modified' | 'created' | 'name'
type SortDirection = 'asc' | 'desc'

type SortOptionValue =
  | 'modified-desc'
  | 'modified-asc'
  | 'created-desc'
  | 'created-asc'
  | 'name-asc'
  | 'name-desc'

const SORT_OPTIONS: {
  value: SortOptionValue
  field: SortField
  direction: SortDirection
  label: string
  description: string
  icon: LucideIcon
}[] = [
  {
    value: 'modified-desc',
    field: 'modified',
    direction: 'desc',
    label: 'Modified',
    description: 'Newest first',
    icon: LucideArrowDownWideNarrow,
  },
  {
    value: 'modified-asc',
    field: 'modified',
    direction: 'asc',
    label: 'Modified',
    description: 'Oldest first',
    icon: LucideArrowUpNarrowWide,
  },
  {
    value: 'created-desc',
    field: 'created',
    direction: 'desc',
    label: 'Created',
    description: 'Newest first',
    icon: LucideArrowDownWideNarrow,
  },
  {
    value: 'created-asc',
    field: 'created',
    direction: 'asc',
    label: 'Created',
    description: 'Oldest first',
    icon: LucideArrowUpNarrowWide,
  },
  {
    value: 'name-asc',
    field: 'name',
    direction: 'asc',
    label: 'Name',
    description: 'A → Z',
    icon: LucideArrowUpNarrowWide,
  },
  {
    value: 'name-desc',
    field: 'name',
    direction: 'desc',
    label: 'Name',
    description: 'Z → A',
    icon: LucideArrowDownWideNarrow,
  },
]

export default function MapSavedSearches({
  mapRef,
}: {
  mapRef: React.RefObject<AppMapRef>
}) {
  const user = useAtomValue(aUser)
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [sortOption, setSortOption] = useLocalStorage<SortOptionValue>(
    `saved-searches-sort-${user?._id || 'default'}`,
    'modified-desc'
  )
  const { field: sortField, direction: sortDirection } = SORT_OPTIONS.find(
    (option) => option.value === sortOption
  )!
  const searchQueryKey = [
    'saved-searches',
    user?._id || '',
    `sort:${sortField}-${sortDirection}`,
    'disable-cache',
  ]
  const sortArgument =
    sortField === 'created' || sortField === 'modified'
      ? { [sortField]: sortDirection === 'desc' ? -1 : 1 }
      : undefined
  const {
    searches: fetchedSearches,
    isLoading,
    refetch: refetchSearches,
  } = useSearchesQuery({
    key: searchQueryKey,
    find: { 'user._id': user?._id },
    sort: sortArgument,
    enabled: !!user,
  })
  const displayedSearches = useMemo(() => {
    if (fetchedSearches.length === 0) return fetchedSearches
    if (sortField === 'name') {
      return [...fetchedSearches].sort((a, b) =>
        sortDirection === 'desc'
          ? b.name.localeCompare(a.name)
          : a.name.localeCompare(b.name)
      )
    }
    if (sortField === 'created') {
      return [...fetchedSearches].sort((a, b) => {
        const aCreated = new Date(a.created ?? 0).getTime()
        const bCreated = new Date(b.created ?? 0).getTime()
        return sortDirection === 'desc'
          ? bCreated - aCreated
          : aCreated - bCreated
      })
    }
    if (sortField === 'modified') {
      return [...fetchedSearches].sort((a, b) => {
        const aModified = new Date(
          (a as { modified?: string }).modified ?? a.created ?? 0
        ).getTime()
        const bModified = new Date(
          (b as { modified?: string }).modified ?? b.created ?? 0
        ).getTime()
        return sortDirection === 'desc'
          ? bModified - aModified
          : aModified - bModified
      })
    }
    return fetchedSearches
  }, [fetchedSearches, sortField, sortDirection])
  const hasSearches = fetchedSearches.length > 0

  if (!user) {
    return null
  }

  return (
    <Sheet
      modal={false}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
      }}>
      <ControlTooltip text="Saved Searches" side="bottom">
        <Sheet.Trigger asChild>
          <Button
            variant="map"
            size="map"
            className="shadow-md xl:w-auto xl:px-3">
            <LucideStar className="h-5 w-5" />
            <span className="ml-2 hidden xl:inline-flex">Searches</span>
          </Button>
        </Sheet.Trigger>
      </ControlTooltip>
      <Sheet.Content
        className="flex flex-col"
        side="right"
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation()
          }
        }}>
        <Sheet.Header>
          <div className="item-center -mt-1 flex justify-between pr-6">
            <Sheet.Title className="">Saved Searches</Sheet.Title>
            <SavedSearchesDialog
              key={fetchedSearches.length}
              onSearchCreate={() => {
                if (!isLoading) {
                  refetchSearches()
                }
              }}
            />
          </div>
          <Sheet.Description>
            Save the current map view and filters, update existing saved
            searches, and subscribe to changes.
          </Sheet.Description>
          {hasSearches && (
            <div className="relative !mt-4">
              <div className="flex gap-1">
                <div className="relative flex-[6]">
                  <Input
                    placeholder="Filter searches..."
                    className="h-9 pr-10"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  {filter.length > 0 && (
                    <Button
                      size="none"
                      variant="ghost"
                      onClick={() => setFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 transform !bg-background text-muted-foreground">
                      <LucideX className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Popover>
                  <Popover.Trigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2">
                      <LucideArrowUpDown className="h-4 w-4" />
                      Sort
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content
                    align="end"
                    className="w-auto min-w-[200px] p-0">
                    <div className="flex flex-col gap-1">
                      {SORT_OPTIONS.map((option) => {
                        const isActive = option.value === sortOption
                        const { label, description } = option
                        return (
                          <Button
                            key={option.value}
                            variant="ghost"
                            size="sm"
                            className={cn(
                              'justify-start gap-2 rounded-none py-2',
                              isActive && 'bg-muted/50 hover:bg-muted'
                            )}
                            onClick={() => setSortOption(option.value)}>
                            <span className="w-4">
                              {isActive && <LucideCheck className="h-4 w-4" />}
                            </span>

                            <span className="whitespace-nowrap text-sm">
                              {label}: {description}
                            </span>
                          </Button>
                        )
                      })}
                    </div>
                  </Popover.Content>
                </Popover>
              </div>
            </div>
          )}
        </Sheet.Header>
        <div className="flex-1 overflow-auto">
          {isLoading && fetchedSearches.length === 0 ? (
            <div className="mt-8 flex flex-col items-center justify-center">
              <LoadingCircle className="h-6 w-6 text-primary" />
              <span className="mt-2">Loading</span>
            </div>
          ) : (
            <SavedSearchesList
              searchQueryKey={searchQueryKey}
              filter={filter}
              searches={displayedSearches}
              mapRef={mapRef}
              setIsOpen={setIsOpen}
            />
          )}
        </div>
      </Sheet.Content>
    </Sheet>
  )
}
